import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PlaidLinkOnExit,
  PlaidLinkOnSuccess,
  PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link';
import { Prompt } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Registration } from '@stax-core/types-omni';
import { RegistrationForm } from '../../@types';
import { coreapi, onboardingapi } from '../../api';
import {
  AuthStore,
  BrandStore,
  RegistrationStore,
  RoutesContext,
  updateRegistration,
} from '../../context';
import {
  formatFirstAndLastNameToFullName,
  formatRoutingNumber,
} from '../../util/format.util';
import { handlePlaidLinkError } from '../../util/plaid.util';
import { isMeaningfulString } from '../../util/validator.util';
import { useToaster, useWriteableFieldFormValidation } from '../../hooks';
import {
  FieldError,
  Group,
  Input,
  ToggleableField,
  ToggleableFieldProps,
} from '../../components';
import { CenteredContent } from '../components/CenteredContent';
import { BackButton } from '../components/BackButton';
import { NextButton } from '../components/NextButton';
import { MainForm } from '../components/application/MainForm';
import { PlaidAccountCards, PlaidLink } from '../../components/Plaid';
import {
  FinancialFieldsProps,
  PlaidAccountBase,
  PlaidAccountType,
} from '../../pages/financial/FinancialFields.types';
import { hasPrimaryManualBankAccount } from '../../pages/financial/FinancialFields.util';

interface FieldValues
  extends Pick<
    Registration,
    'bank_account_number' | 'bank_account_owner_name' | 'bank_routing_number'
  > {
  showManualAccountFields: boolean;
}

interface AccountFormProps extends RegistrationForm<FieldValues> {
  showManualAccountFields: boolean;
  setShowManualAccountFields: React.Dispatch<React.SetStateAction<boolean>>;
  route?: Pick<RoutesContext, 'currentRoute' | 'nextRoute' | 'previousRoute'>;
}

const TextLinkButton = styled.button`
  appearance: none;
  font-size: 0.9em;
  color: #009bf2;
  text-decoration: underline;
  cursor: pointer;
  border: none;
  background: inherit;
`;

/**
 * The fields for the 'Deposit account' page including link accounts and manually entered accounts
 */
const AccountFields: FC<AccountFormProps> = props => {
  const {
    form: { mutators },
    values,
    showManualAccountFields,
    setShowManualAccountFields,
  } = props;
  const { state: { auth } } = useContext(AuthStore);
  const { state: { brand } } = useContext(BrandStore);
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const [linkToken, setLinkToken] = useState<string | null>();
  const [plaidAccounts, setPlaidAccounts] = useState<PlaidAccountBase[]>([]);
  // set to true when loading, adding, or removing an account to display loading UI
  const [updatingAccounts, setUpdatingAccounts] = useState(true);
  const { toaster, toast } = useToaster();
  const allowPlaid = brand?.allowPlaid;
  const merchantId = auth?.merchant?.id;
  const userId = auth?.user?.id;
  const authToken = auth?.token ?? null;
  const authTokenRef = useRef<string>();
  authTokenRef.current = authToken!; // keep the reference updated
  const valuesShowManualAccountFields = values.showManualAccountFields;
  const hasPrimaryManualAccount =
    plaidAccounts.length === 0 &&
    registration &&
    hasPrimaryManualBankAccount(registration);

  // keep the 'showManualAccountFields' form value updated since there's no UI input field for that form value
  useEffect(() => {
    const show = showManualAccountFields || hasPrimaryManualAccount;
    if (valuesShowManualAccountFields !== show) {
      mutators.setFieldValue('showManualAccountFields', show);
    }
  }, [
    mutators,
    hasPrimaryManualAccount,
    valuesShowManualAccountFields,
    showManualAccountFields,
  ]);

  const fetchAndSetAccountNumbers = useCallback(async () => {
    if (!allowPlaid) {
      return;
    }

    const accountsResponse: { accounts: PlaidAccountType[] } =
      await onboardingapi.get(
        authTokenRef.current!,
        '/banking/accounts?include_institution=true',
      );

    if (accountsResponse?.accounts) {
      const maskedAccounts =
        accountsResponse.accounts?.map(account => ({
          mask: account.mask,
          name: account.name,
          plaid_token_id: account.plaid_token_id,
          account_type: account.account_type,
          logo: account.institution?.logo,
        })) ?? [];

      setPlaidAccounts(maskedAccounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowPlaid,
    setPlaidAccounts,
    // see comment for the verifyBank() useEffect() below and how it uses merchantId and userId as deps instead of authToken
    merchantId,
    registration,
  ]);

  useEffect(() => {
    if (!allowPlaid) {
      return;
    }
    // Plaid OAuth callback handling, based on:
    // https://github.com/plaid/react-plaid-link
    const isOAuthRedirect = window.location.href.includes('?oauth_state_id');
    if (isOAuthRedirect) {
      setLinkToken(localStorage.getItem(`linkToken.${merchantId}`)!);
      setUpdatingAccounts(false);
      setShowManualAccountFields(false);
      // remove the Plaid 'oauth_state_id' search parameter from the URL so that navigating back to this page does not re-trigger it
      const routePath = props.route?.currentRoute?.path;
      if (routePath) {
        window.history.replaceState(null, '', '/#' + routePath);
      }
      return;
    }
    // otherwise (regular flow), create a Plaid link
    const verifyBank = async () => {
      try {
        setUpdatingAccounts(true);
        const { token } = await onboardingapi.get(
          authTokenRef.current!,
          '/banking/verify',
        );
        localStorage.setItem(`linkToken.${merchantId}`, token);
        setLinkToken(token);
        await fetchAndSetAccountNumbers();
        setUpdatingAccounts(false);
        setShowManualAccountFields(false);
      } catch (error) {
        setUpdatingAccounts(false);
        setShowManualAccountFields(true);
        toaster(
          toast.error(
            error?.message ??
              'There was a problem retrieving your account information',
          ),
        );
      }
    };
    verifyBank();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowPlaid,
    fetchAndSetAccountNumbers,
    setShowManualAccountFields,
    toaster,
    toast,
    // these deps are included instead of 'authToken' and 'authTokenRef' is used inside this effect because authToken is asynchronously
    // refreshed in App.tsx when the page loads, a race condition can occur and a new auth token can be dispatch()'ed to AuthStore after
    // this effect has rendered that triggers this effect to re-run because the authToken is different but the data in the authToken that
    // the '/banking/verify' route uses (merchantId and userId) may not have changed, so use merchantId and userId as deps instead of
    // authToken to prevent the unnecessary re-run of this effect and service calls.
    merchantId,
    userId,
  ]);

  // The usePlaidLink() hook in PlaidLink.tsx manages Plaid Link creation
  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    async (public_token: string, metadata: PlaidLinkOnSuccessMetadata) => {
      setLinkToken(null);
      setUpdatingAccounts(true);
      try {
        await onboardingapi.post(authToken!, '/banking/token/exchange', {
          public_token,
          metadata,
        });
        const { token } = await onboardingapi.get(authToken!, '/banking/verify');
        localStorage.setItem(`linkToken.${merchantId}`, token);
        setLinkToken(token);
        await fetchAndSetAccountNumbers();
        const r = await coreapi.get(authToken!, '/team/registration');
        dispatch(updateRegistration(r));
      } catch (error: any) {
        toaster(toast.error('There was a problem linking your account'));
      }
      setUpdatingAccounts(false);
    },
    [
      authToken,
      merchantId,
      fetchAndSetAccountNumbers,
      dispatch,
      toaster,
      toast,
    ],
  );

  const onExit = useMemo<PlaidLinkOnExit>(
    () =>
      handlePlaidLinkError(authToken!, (_, type) => {
        // do nothing based on figma, let user click the 'add bank manually' button
      }),
    [authToken],
  );

  const accounts = plaidAccounts.concat(
    hasPrimaryManualAccount ? getManualAccounts(registration) : [],
  );

  const defaultFieldProps: Partial<ToggleableFieldProps<any, HTMLElement>> = {
    toggleable: props.mode === 'review',
    defaultMode: props.mode === 'review' ? 'display' : 'edit',
  };

  return (
    <>
      {updatingAccounts && <AccountSkeletonLoader />}

      {accounts.length > 0 && (
        <PlaidAccountCards
          accounts={accounts}
          updateAccountNumbers={fetchAndSetAccountNumbers}
          setUpdatingAccounts={setUpdatingAccounts}
          updatingAccounts={updatingAccounts}
          setLinkToken={setLinkToken}
        />
      )}

      {!updatingAccounts && !showManualAccountFields && accounts.length === 0 && (
        <>
          <div>
            <PlaidLink
              linkToken={linkToken!}
              onSuccess={onSuccess}
              onExit={onExit}
              text={'+ Add via Bank Login'}
            />
          </div>
          <div style={{ marginBottom: '2em', textAlign: 'center' }}>
            <TextLinkButton
              id="account-link-button"
              data-testid="account-link-button"
              onClick={event => {
                event.preventDefault();
                setShowManualAccountFields(true);
              }}
            >
              Add Bank Manually
            </TextLinkButton>
          </div>
        </>
      )}

      {/** Show manual fields if applicable */}
      {showManualAccountFields && accounts.length === 0 && (
        <div data-testid="financial-institution-primary-group">
          <Group direction="column">
            <ToggleableField
              name="bank_account_owner_name"
              label="Name on the Account"
              {...defaultFieldProps}
            >
              {props => <Input {...props.input} />}
            </ToggleableField>
            <FieldError name="bank_account_owner_name" />
          </Group>

          <Group direction="column">
            <ToggleableField
              name="bank_routing_number"
              label="Routing Number"
              parse={formatRoutingNumber}
              format={formatRoutingNumber}
              {...defaultFieldProps}
            >
              {props => (
                <Input {...props.input} autoComplete="off" maxLength={9} />
              )}
            </ToggleableField>
            <FieldError name="bank_routing_number" />
          </Group>

          <Group direction="column">
            <ToggleableField
              name="bank_account_number"
              label="Account Number"
              {...defaultFieldProps}
            >
              {props => (
                <Input {...props.input} autoComplete="off" type="number" />
              )}
            </ToggleableField>
            <FieldError name="bank_account_number" />
          </Group>
        </div>
      )}

      {props.children}
    </>
  );
};

export const AccountSkeletonLoader = () => {
  return (
    <div className={'skeleton-loader'}>
      <Skeleton count={2} height="2rem" width="90%" />
    </div>
  );
};

export const DepositAccountPage: FC<FinancialFieldsProps> = () => {
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const { toaster, toast } = useToaster();
  const [showManualAccountFields, setShowManualAccountFields] = useState(false);
  const {
    state: { auth },
  } = useContext(AuthStore);
  const authToken = auth?.token ?? null;

  const onSubmit = useCallback(
    async (formValues: FieldValues) => {
      const payload: Partial<Registration> = {
        bank_account_number: formValues.bank_account_number,
        bank_account_owner_name: formValues.bank_account_owner_name,
        bank_routing_number: formValues.bank_routing_number,
      };

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));
        setShowManualAccountFields(false);
        return true;
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toaster, toast],
  );

  const validate = useWriteableFieldFormValidation((values: FieldValues) => {
    const errors: Partial<Record<keyof FieldValues, any>> = {};
    if (!values.bank_account_number) {
      errors.bank_account_number = 'A bank account is required';
    }
    if (values.showManualAccountFields) {
      if (!isMeaningfulString(values.bank_account_owner_name)) {
        errors.bank_account_owner_name = 'Not a valid name';
      }
      if (values.bank_routing_number?.length !== 9) {
        errors.bank_routing_number = 'Must be 9 characters';
      }
    }
    return errors;
  });

  return (
    <MainForm<FieldValues, Partial<Registration>>
      initialValues={mapInitialFormValues(registration)}
      validate={validate}
      onSubmit={onSubmit}
      submitAction={showManualAccountFields ? 'nothing' : null}
    >
      {formProps => (
        <CenteredContent
          id="financial-accounts"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <BackButton id="financial-accounts-back" onClick={showManualAccountFields
            ? (evt) => {
                setShowManualAccountFields(false);
                evt.preventDefault();
              }
            : null as any}
          />
          <h2>Deposit account</h2>
          <div>
            <p>
              Please provide a business checking account where you would like
              your funds deposited.
            </p>
            <AccountFields
              mode="edit"
              showManualAccountFields={showManualAccountFields}
              setShowManualAccountFields={setShowManualAccountFields}
              {...formProps}
            >
              <Prompt
                when={formProps.dirty && !formProps.submitting}
                message="You have unsaved changes; are you sure you want to leave without saving?"
              />
            </AccountFields>
          </div>
          <NextButton id="financial-accounts-continue" formProps={formProps} />
        </CenteredContent>
      )}
    </MainForm>
  );
};

function getManualAccounts(registration: Registration): PlaidAccountBase[] {
  const accounts: PlaidAccountBase[] = [];
  if (registration && hasPrimaryManualBankAccount(registration)) {
    accounts.push({
      mask: registration.bank_account_number?.slice(-4) ?? '',
      name: registration.bank_account_owner_name ?? '',
      plaid_token_id: null,
      account_type: 'primary',
    });
  }
  return accounts;
}

function mapInitialFormValues(
  registration: Registration | null,
): Partial<FieldValues> {
  /** Copied from FinancialFields.util mapRegistrationToInitialFormValues() */
  return registration
    ? {
        bank_account_owner_name:
          registration.bank_account_owner_name ||
          formatFirstAndLastNameToFullName(
            registration.first_name,
            registration.last_name,
          ),
        bank_account_type: registration.bank_account_type,
        bank_routing_number: registration.bank_routing_number,
        bank_account_number: registration.bank_account_number,
        showManualAccountFields: false,
      }
    : ({} as any);
}
